import { Button, Heading } from '@mm/components';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { snake } from '@mm/state/helpers/converterString';
import {
  MyLeadsAttributes,
  myLeadsAttributesBasedTypes,
} from '@mm/state/interfaces/myLeads';
import { isBilling2023V2Subscription } from '@mm/state/model/SubscriptionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { snakeCase } from 'lodash';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import ObjectUtils from '../../../../../common/helpers/utils/object-utils';
import { AudienceListAccordions } from './AudienceListAccordions/AudienceListAccordions';
import styles from './Audiences.module.css';
import { ContactsByChannelsStatistic } from './ContactsByChannelsStatistic/ContactsByChannelsStatistic';
import { LeadsContactsToolbarButtons } from './LeadsContactsToolbarButtons';
import { useFetchAudience } from './use-fetch-audience';
import { useLeadContactsFilter } from './use-lead-contacts-filter';

export const PAGE_SIZE = 10;

export const getESStructure = (esFilters?: MyLeadsAttributes) => {
  if (!esFilters) return;
  const {
    attributes,
    excludedAttributes,
    prefixAttributes,
    excludedPrefixAttributes,
    wildcardAttributes,
    excludedWildcardAttributes,
    ...restVariables
  } = esFilters || {};

  const buildSnakeAttributes = filters => {
    const snakeAttributes = {};
    myLeadsAttributesBasedTypes.forEach(key => {
      if (filters[key]) {
        snakeAttributes[key] = snake(filters[key]);
      }
    });
    return snakeAttributes;
  };

  const snakeAttributes = buildSnakeAttributes({
    attributes,
    excludedAttributes,
    prefixAttributes,
    excludedPrefixAttributes,
    wildcardAttributes,
    excludedWildcardAttributes,
  });

  return {
    ...snakeAttributes,
    ...Object.entries(restVariables)
      .flatMap(([key, value]) => ({
        [snakeCase(key).toLowerCase()]: ObjectUtils.deepSnakeKeys(
          value || {},
          true,
          true,
        ),
      }))
      .reduce((result, item) => ({ ...result, ...item }), {}),
  };
};

export const Audiences = () => {
  const { activeAudienceId } = useLeadContactsFilter();
  const { retrieveAll } = useContactsCRUDActions();
  const facebookPage = useSelector(selectActiveFacebookPage);
  const allContacts = useSelector(selectContacts);

  const { busy } = useFetchAudience(activeAudienceId, {
    handleError: true,
    options: { skipBusy: true },
    queryParams: { inUse: true },
  });

  const modalActions = useModalActions();
  const { showSlideoutMenu } = useViewActions();

  const processClick = (onSuccess: (args?: any) => void) => {
    if (
      facebookPage?.subscription &&
      facebookPage?.subscription?.failedPayment &&
      isBilling2023V2Subscription(facebookPage.subscription)
    ) {
      modalActions.openAccountLockedModal();
    } else {
      onSuccess();
    }
  };

  const openFiltersSlideout = () => {
    showSlideoutMenu('myLeadsFilters');
  };

  useEffect(() => {
    if (facebookPage) {
      retrieveAll(facebookPage?.id);
    }
  }, [facebookPage?.id]);

  return (
    <section className={styles.container}>
      <div className={styles.contactsPane}>
        <div className={styles.mainToolbar}>
          <div className={styles.mainToolbarLeft}>
            <Heading size={3} className={styles.audienceName}>
              Audiences
            </Heading>
          </div>

          <LeadsContactsToolbarButtons processClick={processClick} />
        </div>

        <div className={styles.analyticsContainer}>
          <Button
            color="blue"
            css={{ container: styles.save }}
            onClick={openFiltersSlideout}
          >
            Build an Audience
          </Button>

          <div className={styles.analyticsLeadsCount}>
            Leads {allContacts.allContactsCount?.toLocaleString()}
          </div>
          <ContactsByChannelsStatistic
            css={{
              container: styles.statsContainer,
              row: styles.statRow,
              data: styles.statData,
              count: styles.statCount,
            }}
          />
        </div>
        <AudienceListAccordions
          processClick={processClick}
          onCreateClick={openFiltersSlideout}
          activeAudienceId={activeAudienceId}
          busy={busy}
        />
      </div>
    </section>
  );
};
